import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/feature/Footer';
import StructuredData from '../../components/feature/StructuredData';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  tags: string[];
  date: string;
  image: string;
  slug: string;
}

const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://hudosan-shobun.com";

  useEffect(() => {
    document.title = '負動産処分コラム｜山林・農地・空き家の処分方法を弁護士が解説';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '相続した山林、農地、空き家などの負動産処分に関する法律コラム。相続土地国庫帰属制度の申請方法、農地転用の手続き、共有名義の解消方法など、弁護士がわかりやすく解説します。');
    }
  }, []);

  const blogPosts: BlogPost[] = [
    {
      id: '1',
      title: '相続した山林がいらない…愛知県で処分する5つの方法',
      excerpt: '相続した山林を処分する5つの方法を弁護士が解説。隣接地所有者への譲渡、相続土地国庫帰属制度、弁護士による法的処分など。',
      category: '山林・原野',
      tags: ['山林', '相続', '処分', '愛知県', '固定資産税'],
      date: '2026-01-15',
      image: 'https://public.readdy.ai/ai/img_res/946ffded8703ac9cf28ad64b6940d007.jpg',
      slug: 'sanrin-syobun-5-houhou'
    },
    {
      id: '2',
      title: '農地を相続したくない場合の5つの選択肢｜弁護士が手続きと注意点を解説',
      excerpt: '農地を相続したくない方に向けて、相続放棄、遺産分割、売却、転用、国庫帰属制度など5つの選択肢を弁護士が詳しく解説。農地法の規制と手続き上の注意点も説明します。',
      category: '農地',
      tags: ['農地', '相続', '相続放棄', '農地法', '転用'],
      date: '2026-01-20',
      image: 'https://readdy.ai/api/search-image?query=Abandoned%20neglected%20farmland%20agricultural%20field%20with%20overgrown%20weeds%20and%20untended%20crops%2C%20unmaintained%20rural%20agricultural%20land%20showing%20signs%20of%20abandonment%2C%20difficult%20to%20manage%20inherited%20farmland%20with%20wild%20vegetation%2C%20desolate%20agricultural%20landscape%20representing%20problematic%20inherited%20property%20in%20Japan%2C%20realistic%20documentary%20photography%20style%20with%20natural%20overcast%20lighting%20emphasizing%20the%20challenging%20nature%20of%20agricultural%20land%20management&width=800&height=450&seq=blog-nouchi-abandoned-002&orientation=landscape',
      slug: 'nouchi-souzoku-5-sentakushi'
    },
    {
      id: '3',
      title: '相続土地国庫帰属制度を自分で申請する方法｜手続きの流れと必要書類を解説',
      excerpt: '相続土地国庫帰属制度の具体的な申請手順を解説。事前相談から承認、負担金納付まで、必要書類と注意点を弁護士が詳しく説明します。',
      category: '法律知識',
      tags: ['国庫帰属', '相続', '手続き', '法務局', '負担金'],
      date: '2026-01-25',
      image: 'https://static.readdy.ai/image/5e182d52a94dfeeda6703180d188c585/7c76bb1e5aefffc2cf36a7486d810c54.jpeg',
      slug: 'kokko-kizon-shinsei-houhou'
    }
  ];

  const categories = ['all', '山林・原野', '農地', '空き家', '相続', '法律知識'];

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "負動産処分コラム",
    "description": "愛知県の負動産処分に関する法律コラム。山林、農地、空き家などの処分方法を弁護士が解説。",
    "url": siteUrl + "/blog",
    "publisher": {
      "@type": "Organization",
      "name": "冨田・島岡法律事務所",
      "logo": {
        "@type": "ImageObject",
        "url": siteUrl + "/logo.png"
      }
    }
  };

  const collectionPageData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "負動産処分コラム｜山林・農地・空き家の処分方法",
    "description": "相続した山林、農地、空き家などの負動産処分に関する法律コラム。弁護士が処分方法や手続きを詳しく解説します。",
    "url": siteUrl + "/blog",
    "isPartOf": {
      "@type": "WebSite",
      "name": "冨田・島岡法律事務所",
      "url": siteUrl
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": blogPosts.map((post, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": siteUrl + "/blog/" + post.slug,
        "name": post.title
      }))
    }
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "ホーム",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "コラム",
        "item": siteUrl + "/blog"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <StructuredData data={structuredData} />
      <StructuredData data={collectionPageData} />
      <StructuredData data={breadcrumbData} />
      
      {/* シンプルなヘッダー */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex flex-col cursor-pointer">
            <span className="font-noto text-xl font-bold text-navy-800">冨田・島岡法律事務所</span>
            <span className="font-en text-[10px] tracking-wider text-navy-600">TOMIDA SHIMAOKA LAW OFFICE</span>
          </Link>
          <Link to="/" className="text-navy-600 hover:text-navy-800 font-noto text-sm cursor-pointer">
            <i className="ri-home-line mr-1"></i>
            トップページ
          </Link>
        </div>
      </header>
      
      <main className="pt-12 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* ヘッダー */}
          <div className="text-center mb-12">
            <h1 className="font-noto text-4xl md:text-5xl font-bold text-navy-900 mb-4">
              負動産処分コラム
            </h1>
            <p className="font-noto text-lg text-navy-700">
              山林・農地・空き家などの処分方法を弁護士が解説
            </p>
          </div>

          {/* 専門ページへの誘導 */}
          <div className="mb-12 bg-white rounded-xl p-6 border border-beige-200">
            <h2 className="font-noto text-lg font-bold text-navy-900 mb-4 text-center">
              お悩み別の専門ページ
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/kyoyu-meigi"
                className="inline-flex items-center gap-2 px-5 py-3 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer"
              >
                <i className="ri-group-line"></i>
                共有名義・相続未分割
              </Link>
              <Link
                to="/nouchi"
                className="inline-flex items-center gap-2 px-5 py-3 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer"
              >
                <i className="ri-seedling-line"></i>
                農地処分
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white text-navy-700 border border-navy-300 rounded-lg hover:bg-navy-50 transition-colors cursor-pointer"
              >
                <i className="ri-home-line"></i>
                負動産処分トップ
              </Link>
            </div>
          </div>

          {/* カテゴリフィルター */}
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full font-noto text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-navy-700 text-white'
                      : 'bg-white text-navy-700 border border-navy-200 hover:border-navy-400'
                  }`}
                >
                  {category === 'all' ? 'すべて' : category}
                </button>
              ))}
            </div>
          </div>

          {/* 記事一覧 */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="bg-white rounded-lg overflow-hidden border border-beige-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-beige-100 text-navy-700 text-xs font-medium rounded-full">
                      {post.category}
                    </span>
                    <span className="text-navy-500 text-sm">
                      {new Date(post.date).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <h2 className="font-noto text-xl font-bold text-navy-900 mb-3 line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="font-noto text-navy-700 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs text-navy-600 bg-beige-50 px-2 py-1 rounded"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-20">
              <p className="font-noto text-navy-600 text-lg">
                該当する記事が見つかりませんでした
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;